<template>
  <section
    v-if="darfMailEinstellungenVerwalten"
    class="mt-8"
  >
    <v-divider class="mb-6"></v-divider>

    <div class="d-flex flex-wrap align-center mb-2">
      <div>
        <p class="text-title text--secondary mb-1">
          E-Mail-Versand und Signatur
        </p>
        <p class="text-caption text--secondary mb-0">
          Diese Einstellungen gelten für alle E-Mails des Auftrags- und Vertragsmoduls.
          Das SMTP-Passwort wird niemals angezeigt.
        </p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        small
        text
        color="primary"
        class="mt-2"
        :loading="loading"
        :disabled="requestRunning"
        @click="requestReload"
      >
        <v-icon left>
          mdi-refresh
        </v-icon>
        Neu laden
      </v-btn>
    </div>

    <v-alert
      v-if="message"
      dense
      text
      class="mt-4"
      :type="messageType"
    >
      {{ message }}
    </v-alert>

    <v-skeleton-loader
      v-if="loading && !loaded"
      class="mt-4"
      type="article, actions"
    ></v-skeleton-loader>

    <template v-else-if="loaded">
      <v-card
        outlined
        class="mt-4"
      >
        <v-card-text>
          <div class="d-flex flex-wrap align-center">
            <v-chip
              small
              dark
              class="mr-2 mb-2"
              :color="summary.smtpConfigured ? 'success' : 'warning'"
            >
              <v-icon left small>
                {{ summary.smtpConfigured ? 'mdi-email-check' : 'mdi-email-alert' }}
              </v-icon>
              {{ summary.smtpConfigured ? 'SMTP konfiguriert' : 'SMTP unvollständig' }}
            </v-chip>
            <v-chip
              small
              outlined
              class="mr-2 mb-2"
              :color="summary.passwordConfigured ? 'success' : 'warning'"
            >
              <v-icon left small>
                {{ summary.passwordConfigured ? 'mdi-lock-check' : 'mdi-lock-alert' }}
              </v-icon>
              {{ summary.passwordConfigured ? 'Passwort hinterlegt' : 'Kein Passwort hinterlegt' }}
            </v-chip>
            <v-chip
              small
              outlined
              class="mr-2 mb-2"
              :color="lastTestColor"
            >
              <v-icon left small>
                {{ lastTestIcon }}
              </v-icon>
              {{ lastTestLabel }}
            </v-chip>
          </div>

          <v-row
            dense
            class="mt-1"
          >
            <v-col
              cols="12"
              md="6"
            >
              <span class="text-caption text--secondary">Vorschau des Absenders</span><br>
              <span>{{ currentSenderLabel }}</span>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <span class="text-caption text--secondary">Revision</span><br>
              <span>{{ revisionLabel }}</span>
            </v-col>
            <v-col
              v-if="lastTest.message"
              cols="12"
            >
              <span class="text-caption text--secondary">Letzter Test</span><br>
              <span>{{ lastTest.message }}</span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-form
        ref="settingsForm"
        v-model="formValid"
        class="mt-6"
        @submit.prevent="saveSettings"
      >
        <p class="text-subtitle-1 font-weight-medium mb-2">
          Absender und SMTP-Postfach
        </p>

        <v-row dense>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="form.smtp.name"
              label="Absendername"
              maxlength="120"
              counter="120"
              :disabled="requestRunning"
              :rules="senderNameRules"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="form.smtp.mail"
              label="Absender-E-Mail-Adresse"
              type="email"
              maxlength="254"
              autocomplete="email"
              :disabled="requestRunning"
              :rules="senderMailRules"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="form.smtp.host"
              label="SMTP-Host"
              maxlength="253"
              autocomplete="off"
              :disabled="requestRunning"
              :rules="hostRules"
            />
          </v-col>
          <v-col
            cols="12"
            sm="4"
            md="2"
          >
            <v-text-field
              v-model.number="form.smtp.port"
              label="Port"
              type="number"
              min="1"
              max="65535"
              :disabled="requestRunning"
              :rules="portRules"
            />
          </v-col>
          <v-col
            cols="12"
            sm="8"
            md="4"
          >
            <v-select
              v-model="form.smtp.secure"
              :items="secureOptions"
              label="Verschlüsselung"
              :disabled="requestRunning"
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              v-model="form.smtp.auth"
              class="mt-0"
              label="SMTP-Authentifizierung verwenden"
              :disabled="requestRunning"
            ></v-checkbox>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="form.smtp.user"
              label="SMTP-Benutzername"
              maxlength="255"
              autocomplete="username"
              :disabled="requestRunning || !form.smtp.auth"
              :rules="smtpUserRules"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="smtpPassword"
              label="Neues SMTP-Passwort"
              :type="showSmtpPassword ? 'text' : 'password'"
              :append-icon="showSmtpPassword ? 'mdi-eye-off' : 'mdi-eye'"
              autocomplete="new-password"
              :disabled="requestRunning || !form.smtp.auth"
              :rules="smtpPasswordRules"
              :hint="smtpPasswordHint"
              persistent-hint
              @click:append="showSmtpPassword = !showSmtpPassword"
            />
          </v-col>
        </v-row>

        <p class="text-subtitle-1 font-weight-medium mt-6 mb-2">
          Dokumentkopien
        </p>
        <p class="text-caption text--secondary mb-3">
          Legen Sie für jede Dokumentart getrennt fest, ob nach der erfolgreichen
          SMTP-Übergabe an den Kunden interne Kopien mit demselben PDF-Anhang versendet
          werden. Neue Dokumentarten erscheinen hier automatisch, sobald sie im System
          hinterlegt sind. Eine Regel wird wirksam, sobald die jeweilige Dokumentart vom
          System erzeugt und per E-Mail an einen Kunden versendet wird.
        </p>
        <v-alert
          dense
          text
          type="info"
          class="mb-4"
        >
          Mehrere Empfänger sind möglich. Trennen Sie die Adressen mit einem
          Zeilenumbruch, Komma oder Semikolon. Der Kopieversand wird unabhängig von der
          Kundenmail verarbeitet und erneut versucht, ohne den Kunden doppelt anzuschreiben.
        </v-alert>

        <v-row dense>
          <v-col
            v-for="documentCopy in form.documentCopies"
            :key="documentCopy.documentTypeId"
            cols="12"
            md="6"
          >
            <v-card
              outlined
              class="fill-height"
            >
              <v-card-title class="text-subtitle-1 pb-1">
                <v-icon
                  left
                  color="primary"
                >
                  mdi-file-document-outline
                </v-icon>
                {{ documentTypeLabel(documentCopy.documentTypeId) }}
                <v-spacer></v-spacer>
                <v-chip
                  small
                  :color="documentCopy.enabled ? 'success' : 'grey'"
                  :outlined="!documentCopy.enabled"
                  :dark="documentCopy.enabled"
                >
                  {{ documentCopyStatusLabel(documentCopy) }}
                </v-chip>
              </v-card-title>
              <v-card-text>
                <v-switch
                  v-model="documentCopy.enabled"
                  class="mt-1"
                  label="Kopie nach erfolgreicher SMTP-Übergabe senden"
                  :disabled="requestRunning"
                  @change="prepareDocumentCopy(documentCopy)"
                ></v-switch>
                <v-combobox
                  v-model="documentCopy.recipients"
                  :search-input.sync="documentCopy.recipientInput"
                  :items="[]"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hide-no-data
                  append-icon=""
                  label="Empfänger für Kopien"
                  autocomplete="off"
                  :disabled="requestRunning || !documentCopy.enabled"
                  :rules="documentCopyRecipientRules(documentCopy)"
                  hint="Bis zu 20 E-Mail-Adressen; Trennung per Zeilenumbruch, Komma oder Semikolon."
                  persistent-hint
                  @change="normalizeDocumentCopyRecipients(documentCopy)"
                  @keydown="handleRecipientKeydown($event, documentCopy)"
                  @paste="handleRecipientPaste($event, documentCopy)"
                  @blur="commitDocumentCopyRecipient(documentCopy)"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <p class="text-subtitle-1 font-weight-medium mt-6 mb-2">
          E-Mail-Signatur
        </p>
        <p class="text-caption text--secondary mb-3">
          Die Signatur wird an Massen-E-Mails und automatisch erstellte Dokument-E-Mails
          angehängt. Eingefügte Inhalte werden beim Speichern serverseitig bereinigt.
        </p>
        <HtmlEditor
          v-model="form.signatureHtml"
          label="E-Mail-Signatur"
          placeholder="Signatur eingeben ..."
          :disabled="requestRunning"
          @error="handleEditorError"
        />

        <v-alert
          dense
          text
          type="info"
          class="mt-6"
        >
          Zum Testen oder Speichern ist aus Sicherheitsgründen Ihr aktuelles
          Login-Passwort erforderlich. Es wird nur für diesen Vorgang übertragen.
        </v-alert>

        <v-text-field
          ref="currentPassword"
          v-model="currentPassword"
          class="mt-2"
          label="Aktuelles Login-Passwort"
          :type="showCurrentPassword ? 'text' : 'password'"
          :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
          autocomplete="current-password"
          :disabled="requestRunning"
          :rules="currentPasswordRules"
          @click:append="showCurrentPassword = !showCurrentPassword"
        />

        <div class="d-flex flex-wrap justify-end mt-3">
          <v-btn
            color="primary"
            outlined
            class="mr-2 mb-2"
            :loading="testing"
            :disabled="requestRunning || !formValid"
            @click="testSmtp"
          >
            <v-icon left>
              mdi-connection
            </v-icon>
            SMTP testen
          </v-btn>
          <v-btn
            color="success"
            class="mb-2"
            :loading="saving"
            :disabled="requestRunning || !formValid || !hasChanges"
            @click="saveSettings"
          >
            <v-icon left>
              mdi-content-save
            </v-icon>
            Mail-Einstellungen speichern
          </v-btn>
        </div>
      </v-form>

      <v-dialog
        ref="reloadDialogComponent"
        v-model="reloadDialog"
        persistent
        max-width="520"
      >
        <v-card>
          <v-card-title id="mail-reload-dialog-title" class="warning white--text">
            Änderungen verwerfen?
          </v-card-title>
          <v-card-text class="pt-5">
            Beim Neuladen gehen die noch nicht gespeicherten Änderungen an den
            Mail-Einstellungen verloren.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="reloadDialog = false"
            >
              Abbrechen
            </v-btn>
            <v-btn
              color="warning"
              dark
              @click="discardAndReload"
            >
              Änderungen verwerfen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </section>
</template>

<script>
import HtmlEditor from '@/components/Funktionen/400/410-Kunden/HtmlEditor.vue';

function emptySettings() {
  return {
    smtp: {
      name: '',
      mail: '',
      host: '',
      port: 465,
      secure: 'ssl',
      auth: true,
      user: '',
    },
    documentCopies: [],
    signatureHtml: '',
  };
}

const DEFAULT_DOCUMENT_TYPES = [
  { id: 1, name: 'Rechnung', shortName: 'Rechnung' },
  { id: 2, name: 'Angebot', shortName: 'Angebot' },
];

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function validMailAddress(value) {
  const candidate = String(value || '').trim();
  const localPart = candidate.split('@')[0] || '';
  const pattern = /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?)+$/i;
  return candidate.length <= 254 && localPart.length <= 64
    && !localPart.startsWith('.') && !localPart.endsWith('.')
    && !candidate.includes('..') && pattern.test(candidate);
}

function normalizedBoolean(value) {
  if (typeof value === 'string') {
    return value === '1' || value.toLowerCase() === 'true';
  }
  return Boolean(value);
}

function normalizedTestSuccess(value) {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (['success', 'successful', 'ok', 'passed', 'true', '1'].includes(normalized)) {
      return true;
    }
    if (['failed', 'failure', 'error', 'false', '0'].includes(normalized)) {
      return false;
    }
  }
  return Boolean(value);
}

function normalizedDocumentTypeId(value) {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function recipientAddresses(value) {
  const source = Array.isArray(value) ? value : [value];
  const result = [];
  const known = {};
  source.forEach((entry) => {
    String(entry || '').split(/[\n,;]+/).forEach((part) => {
      const address = part.trim();
      const key = address.toLocaleLowerCase('de-DE');
      if (address && !known[key]) {
        known[key] = true;
        result.push(address);
      }
    });
  });
  return result;
}

function normalizedDocumentType(value) {
  if (!value || typeof value !== 'object') return null;
  // In den allgemeinen 410-Einstellungen ist `id` der Tabellen-Primärschlüssel.
  // Für Dokumente ist ausschließlich `nummer` bzw. `documentTypeId` maßgeblich.
  const id = normalizedDocumentTypeId(firstDefined(
    value.documentTypeId,
    value.nummer,
    value.id,
  ));
  if (id === null) return null;
  const name = String(firstDefined(
    value.name,
    value.shortName,
    value.kurz,
    `Dokumentart ${id}`,
  ) || '').trim();
  const shortName = String(firstDefined(
    value.shortName,
    value.kurz,
    name,
  ) || '').trim();
  return {
    id,
    name: name || `Dokumentart ${id}`,
    shortName: shortName || name || `Dokumentart ${id}`,
  };
}

function normalizedDocumentTypes(...sources) {
  const result = [];
  const indexes = {};
  sources.forEach((source) => {
    (Array.isArray(source) ? source : []).forEach((value) => {
      const type = normalizedDocumentType(value);
      if (!type) return;
      if (indexes[type.id] === undefined) {
        indexes[type.id] = result.length;
        result.push(type);
      } else {
        result[indexes[type.id]] = type;
      }
    });
  });
  return result;
}

function normalizedDocumentCopyEntries(source) {
  let entries = [];
  if (Array.isArray(source)) {
    entries = source;
  } else if (source && typeof source === 'object') {
    entries = Object.keys(source).map((documentTypeId) => ({
      ...source[documentTypeId],
      documentTypeId,
    }));
  }
  const result = {};
  entries.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const documentTypeId = normalizedDocumentTypeId(firstDefined(
      entry.documentTypeId,
      entry.document_type_id,
      entry.id,
    ));
    if (documentTypeId === null) return;
    result[documentTypeId] = {
      documentTypeId,
      enabled: normalizedBoolean(entry.enabled),
      recipients: recipientAddresses([
        firstDefined(entry.recipients, entry.mails, entry.mail, []),
        entry.recipientInput || '',
      ]),
      recipientInput: '',
    };
  });
  return result;
}

function responseSummary(result) {
  const candidates = [
    result && result.summary,
    result && result.settings,
    result && result.data && result.data.summary,
    result && result.data && result.data.settings,
    result && result.data,
    result,
  ];
  return candidates.find((candidate) => candidate && typeof candidate === 'object'
    && (candidate.smtp || candidate.fromName !== undefined
      || candidate.smtpConfigured !== undefined)) || null;
}

export default {
  name: 'MailEinstellungen',
  components: {
    HtmlEditor,
  },
  props: {
    documentTypes: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    form: emptySettings(),
    availableDocumentTypes: DEFAULT_DOCUMENT_TYPES.map((type) => ({ ...type })),
    loadedSnapshot: '',
    loaded: false,
    loading: false,
    testing: false,
    saving: false,
    formValid: false,
    smtpPassword: '',
    currentPassword: '',
    showSmtpPassword: false,
    showCurrentPassword: false,
    reloadDialog: false,
    message: '',
    messageType: 'info',
    summary: {
      smtpConfigured: false,
      passwordConfigured: false,
      updatedAt: 0,
      revision: '',
    },
    lastTest: {
      at: 0,
      success: null,
      message: '',
    },
    secureOptions: [
      { text: 'SSL/TLS (meist Port 465)', value: 'ssl' },
      { text: 'STARTTLS (meist Port 587)', value: 'tls' },
      { text: 'Keine Verschlüsselung', value: '' },
    ],
  }),
  computed: {
    darfMailEinstellungenVerwalten() {
      const { save } = this.$store.state.main;
      return Boolean(
        save.user
        && Array.isArray(save.user.rechte)
        && save.user.rechte.includes('s|410|mail')
        && (save.user.rechte.includes('x') || save.user.rechte.includes('x|410|set')),
      );
    },
    requestRunning() {
      return this.loading || this.testing || this.saving;
    },
    normalizedForm() {
      return {
        smtp: {
          name: String(this.form.smtp.name || '').trim(),
          mail: String(this.form.smtp.mail || '').trim(),
          host: String(this.form.smtp.host || '').trim(),
          port: Number(this.form.smtp.port),
          secure: String(this.form.smtp.secure || ''),
          auth: Boolean(this.form.smtp.auth),
          user: String(this.form.smtp.user || '').trim(),
        },
        documentCopies: this.form.documentCopies.map((documentCopy) => ({
          documentTypeId: Number(documentCopy.documentTypeId),
          enabled: Boolean(documentCopy.enabled),
          recipients: this.documentCopyRecipients(documentCopy),
        })),
        signatureHtml: String(this.form.signatureHtml || ''),
      };
    },
    hasChanges() {
      return this.loaded && (
        JSON.stringify(this.normalizedForm) !== this.loadedSnapshot
        || this.smtpPassword !== ''
      );
    },
    senderNameRules() {
      return [
        (value) => Boolean(String(value || '').trim()) || 'Bitte einen Absendernamen angeben.',
        (value) => !/[\r\n\0]/.test(String(value || ''))
          || 'Der Absendername enthält unzulässige Zeichen.',
      ];
    },
    senderMailRules() {
      return [
        (value) => Boolean(String(value || '').trim())
          || 'Bitte eine Absender-E-Mail-Adresse angeben.',
        (value) => validMailAddress(value)
          || 'Bitte eine gültige E-Mail-Adresse angeben.',
      ];
    },
    hostRules() {
      return [
        (value) => Boolean(String(value || '').trim()) || 'Bitte einen SMTP-Host angeben.',
        (value) => !/[\r\n\0]/.test(String(value || ''))
          || 'Der SMTP-Host enthält unzulässige Zeichen.',
      ];
    },
    portRules() {
      return [
        (value) => Number.isInteger(Number(value))
          || 'Bitte einen ganzzahligen SMTP-Port angeben.',
        (value) => (Number(value) >= 1 && Number(value) <= 65535)
          || 'Der SMTP-Port muss zwischen 1 und 65535 liegen.',
      ];
    },
    smtpUserRules() {
      return [
        (value) => !this.form.smtp.auth || Boolean(String(value || '').trim())
          || 'Bitte den SMTP-Benutzernamen angeben.',
      ];
    },
    smtpPasswordRules() {
      return [
        () => !this.form.smtp.auth || this.summary.passwordConfigured
          || this.smtpPassword !== '' || 'Bitte ein SMTP-Passwort angeben.',
      ];
    },
    currentPasswordRules() {
      return [
        (value) => Boolean(value) || 'Bitte Ihr aktuelles Login-Passwort eingeben.',
      ];
    },
    smtpPasswordHint() {
      if (!this.form.smtp.auth) {
        return 'Bei deaktivierter SMTP-Authentifizierung wird kein Passwort verwendet.';
      }
      if (this.summary.passwordConfigured) {
        return 'Leer lassen, um das bereits hinterlegte SMTP-Passwort beizubehalten.';
      }
      return 'Es ist noch kein SMTP-Passwort hinterlegt.';
    },
    currentSenderLabel() {
      const name = String(this.form.smtp.name || '').trim();
      const mail = String(this.form.smtp.mail || '').trim();
      if (name && mail) return `${name} <${mail}>`;
      return mail || name || 'Noch nicht vollständig konfiguriert';
    },
    revisionLabel() {
      const date = this.formatDateTime(this.summary.updatedAt);
      const revision = this.summary.revision;
      if (revision && String(revision) !== String(this.summary.updatedAt)) {
        return date === 'Noch nicht gespeichert' ? String(revision) : `${date} (${revision})`;
      }
      return date;
    },
    lastTestLabel() {
      if (this.lastTest.success === null) return 'Noch nicht getestet';
      const result = this.lastTest.success ? 'Test erfolgreich' : 'Test fehlgeschlagen';
      const date = this.formatDateTime(this.lastTest.at, '');
      return date ? `${result}: ${date}` : result;
    },
    lastTestColor() {
      if (this.lastTest.success === null) return 'grey';
      return this.lastTest.success ? 'success' : 'error';
    },
    lastTestIcon() {
      if (this.lastTest.success === null) return 'mdi-help-circle-outline';
      return this.lastTest.success ? 'mdi-check-circle' : 'mdi-alert-circle';
    },
  },
  watch: {
    documentTypes: {
      handler(types) {
        if (!this.loaded) return;
        const hadChanges = this.hasChanges;
        this.mergeDocumentCopyTypes(types);
        if (!hadChanges) this.loadedSnapshot = JSON.stringify(this.normalizedForm);
      },
      deep: true,
    },
    reloadDialog(open) {
      if (open) this.labelDialogContent('reloadDialogComponent', 'mail-reload-dialog-title');
    },
    darfMailEinstellungenVerwalten(allowed) {
      if (allowed && !this.loaded) {
        this.loadSettings();
      } else if (!allowed) {
        this.clearSecrets();
      }
    },
  },
  methods: {
    documentTypeLabel(documentTypeId) {
      const type = this.availableDocumentTypes.find(
        (item) => Number(item.id) === Number(documentTypeId),
      );
      return type ? (type.name || type.shortName) : `Dokumentart ${documentTypeId}`;
    },
    documentCopyRecipients(documentCopy) {
      return recipientAddresses([
        ...(Array.isArray(documentCopy.recipients) ? documentCopy.recipients : []),
        documentCopy.recipientInput || '',
      ]);
    },
    documentCopyStatusLabel(documentCopy) {
      if (!documentCopy.enabled) return 'Keine Kopien';
      const count = this.documentCopyRecipients(documentCopy).length;
      return count === 1 ? '1 Empfänger' : `${count} Empfänger`;
    },
    documentCopyRecipientRules(documentCopy) {
      return [
        () => !documentCopy.enabled || this.documentCopyRecipients(documentCopy).length > 0
          || 'Bitte mindestens eine Empfängeradresse angeben.',
        () => this.documentCopyRecipients(documentCopy).length <= 20
          || 'Pro Dokumentart sind höchstens 20 Empfänger möglich.',
        () => {
          const invalidAddress = this.documentCopyRecipients(documentCopy)
            .find((address) => !validMailAddress(address));
          return !invalidAddress || `„${invalidAddress}“ ist keine gültige E-Mail-Adresse.`;
        },
      ];
    },
    normalizeDocumentCopyRecipients(documentCopy) {
      documentCopy.recipients = this.documentCopyRecipients(documentCopy);
      documentCopy.recipientInput = '';
    },
    commitDocumentCopyRecipient(documentCopy) {
      if (!String(documentCopy.recipientInput || '').trim()) return;
      this.normalizeDocumentCopyRecipients(documentCopy);
    },
    handleRecipientKeydown(event, documentCopy) {
      if (!event || !['Enter', ',', ';', 'Tab'].includes(event.key)) return;
      if (!String(documentCopy.recipientInput || '').trim()) return;
      if (event.key !== 'Tab') event.preventDefault();
      this.commitDocumentCopyRecipient(documentCopy);
    },
    handleRecipientPaste(event, documentCopy) {
      const clipboard = event && event.clipboardData;
      const pasted = clipboard ? clipboard.getData('text') : '';
      if (!String(pasted || '').trim()) return;
      event.preventDefault();
      documentCopy.recipients = recipientAddresses([
        ...this.documentCopyRecipients(documentCopy),
        pasted,
      ]);
      documentCopy.recipientInput = '';
    },
    prepareDocumentCopy(documentCopy) {
      if (!documentCopy.enabled || this.documentCopyRecipients(documentCopy).length > 0) return;
      const senderMail = String(this.form.smtp.mail || '').trim();
      if (senderMail) documentCopy.recipients = [senderMail];
    },
    mergeDocumentCopyTypes(types = [], configurations = null) {
      const copies = configurations || normalizedDocumentCopyEntries(this.form.documentCopies);
      const configuredTypes = Object.keys(copies).map((documentTypeId) => ({
        id: Number(documentTypeId),
        name: `Dokumentart ${documentTypeId}`,
        shortName: `Dokumentart ${documentTypeId}`,
      }));
      this.availableDocumentTypes = normalizedDocumentTypes(
        configuredTypes,
        DEFAULT_DOCUMENT_TYPES,
        this.availableDocumentTypes,
        this.documentTypes,
        types,
      );
      this.form.documentCopies = this.availableDocumentTypes.map((type) => {
        const existing = copies[type.id];
        return existing || {
          documentTypeId: type.id,
          enabled: false,
          recipients: [],
          recipientInput: '',
        };
      });
    },
    labelDialogContent(refName, titleId) {
      this.$nextTick(() => {
        const dialogComponent = this.$refs[refName];
        const content = dialogComponent && dialogComponent.$refs
          ? dialogComponent.$refs.content : null;
        if (content) content.setAttribute('aria-labelledby', titleId);
      });
    },
    setMessage(type, message) {
      this.messageType = type;
      this.message = message;
    },
    clearSecrets() {
      this.currentPassword = '';
      this.smtpPassword = '';
      this.showCurrentPassword = false;
      this.showSmtpPassword = false;
    },
    async sendRequest(action, fields = {}) {
      if (!this.darfMailEinstellungenVerwalten) {
        throw new Error('Sie sind nicht zur Verwaltung der Mail-Einstellungen berechtigt.');
      }

      const formData = new FormData();
      formData.append('code', this.$store.state.login.session.code);
      formData.append('action', action);
      Object.keys(fields).forEach((field) => {
        formData.append(field, fields[field]);
      });

      let response;
      try {
        response = await fetch(
          `${process.env.VUE_APP_API_HOST}/400/410/massenmail.php`,
          {
            method: 'POST',
            body: formData,
          },
        );
      } catch {
        throw new Error('Der Server konnte nicht erreicht werden. Bitte versuchen Sie es erneut.');
      }

      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('Die Antwort des Mail-Servers konnte nicht verarbeitet werden.');
      }

      if (result.status === 'accessError') {
        this.$store.commit('login/backendErrorSession');
        throw new Error(
          result.message || 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
        );
      }
      if (!response.ok || result.status !== 'success') {
        if (result.status === 'conflict' || response.status === 409) {
          throw new Error(
            result.message
              || 'Die Mail-Einstellungen wurden zwischenzeitlich geändert. Bitte neu laden.',
          );
        }
        throw new Error(result.message || 'Die Mail-Einstellung konnte nicht verarbeitet werden.');
      }
      return result;
    },
    applySummary(summary, updateForm = true) {
      if (!summary || typeof summary !== 'object') return;
      const smtp = summary.smtp && typeof summary.smtp === 'object' ? summary.smtp : summary;

      if (updateForm) {
        const invoiceCopy = summary.invoiceCopy && typeof summary.invoiceCopy === 'object'
          ? summary.invoiceCopy : {};
        const documentCopies = normalizedDocumentCopyEntries(summary.documentCopies);
        if (!documentCopies[1]) {
          const legacyMail = String(firstDefined(
            summary.invoiceCopyMail,
            invoiceCopy.mail,
            summary.fromMail,
            smtp.mail,
            '',
          ) || '').trim();
          documentCopies[1] = {
            documentTypeId: 1,
            enabled: normalizedBoolean(firstDefined(
              summary.invoiceCopyEnabled,
              invoiceCopy.enabled,
              false,
            )),
            recipients: legacyMail ? [legacyMail] : [],
            recipientInput: '',
          };
        }
        this.form = {
          smtp: {
            name: String(firstDefined(summary.fromName, smtp.name, '') || ''),
            mail: String(firstDefined(summary.fromMail, smtp.mail, '') || ''),
            host: String(firstDefined(summary.host, smtp.host, '') || ''),
            port: Number(firstDefined(summary.port, smtp.port, 465)),
            secure: String(firstDefined(summary.secure, smtp.secure, 'ssl') || ''),
            auth: normalizedBoolean(firstDefined(summary.auth, smtp.auth, true)),
            user: String(firstDefined(summary.user, smtp.user, '') || ''),
          },
          documentCopies: [],
          signatureHtml: String(firstDefined(
            summary.signatureHtml,
            summary.signature_html,
            '',
          ) || ''),
        };
        this.mergeDocumentCopyTypes(summary.documentTypes, documentCopies);
      }

      const passwordConfigured = firstDefined(
        summary.passwordConfigured,
        smtp.passwordConfigured,
      );
      const smtpConfigured = firstDefined(summary.smtpConfigured, smtp.smtpConfigured);
      const updatedAt = firstDefined(summary.updatedAt, summary.updated_at);
      const revision = firstDefined(summary.revision, summary.configRevision, updatedAt);
      if (passwordConfigured !== undefined) {
        this.summary.passwordConfigured = normalizedBoolean(passwordConfigured);
      }
      if (smtpConfigured !== undefined) {
        this.summary.smtpConfigured = normalizedBoolean(smtpConfigured);
      }
      if (updatedAt !== undefined) this.summary.updatedAt = updatedAt;
      if (revision !== undefined) this.summary.revision = revision;

      this.applyLastTest(summary);
    },
    applyLastTest(summary) {
      let nested = {};
      if (summary.lastTest && typeof summary.lastTest === 'object') {
        nested = summary.lastTest;
      } else if (summary.test && typeof summary.test === 'object') {
        nested = summary.test;
      }
      const at = firstDefined(
        nested.at,
        nested.testedAt,
        summary.lastTestAt,
        summary.lastTestedAt,
        summary.smtpLastTestAt,
      );
      const success = firstDefined(
        nested.success,
        nested.ok,
        summary.lastTestSuccess,
        summary.lastTestResult,
        summary.smtpLastTestSuccess,
      );
      const message = firstDefined(
        nested.message,
        summary.lastTestMessage,
        summary.smtpLastTestMessage,
      );
      if (at !== undefined) this.lastTest.at = at;
      if (success !== undefined) this.lastTest.success = normalizedTestSuccess(success);
      if (message !== undefined) {
        this.lastTest.message = String(message || '');
      } else if (at !== undefined || success !== undefined) {
        this.lastTest.message = '';
      }
    },
    applyResultSummary(result, updateForm = true) {
      const summary = responseSummary(result);
      if (summary) this.applySummary(summary, updateForm);
    },
    async loadSettings() {
      this.loading = true;
      this.message = '';
      try {
        const result = await this.sendRequest('settingsGet');
        const summary = responseSummary(result);
        if (!summary) {
          throw new Error('Der Server hat keine gültigen Mail-Einstellungen zurückgegeben.');
        }
        this.applySummary(summary);
        this.loadedSnapshot = JSON.stringify(this.normalizedForm);
        this.smtpPassword = '';
        this.loaded = true;
      } catch (error) {
        this.setMessage(
          'error',
          error.message || 'Die Mail-Einstellungen konnten nicht geladen werden.',
        );
      } finally {
        this.loading = false;
      }
    },
    async requestReload() {
      if (this.hasChanges) {
        this.reloadDialog = true;
        return;
      }
      await this.reloadSettings();
    },
    async discardAndReload() {
      this.reloadDialog = false;
      await this.reloadSettings();
    },
    async reloadSettings() {
      this.clearSecrets();
      await this.loadSettings();
    },
    validateForProtectedAction() {
      if (!this.$refs.settingsForm || !this.$refs.settingsForm.validate()) {
        this.setMessage('warning', 'Bitte prüfen Sie die markierten Eingaben.');
        return false;
      }
      if (!this.currentPassword) {
        this.setMessage(
          'warning',
          'Bitte geben Sie Ihr aktuelles Login-Passwort ein.',
        );
        this.$nextTick(() => {
          if (this.$refs.currentPassword) this.$refs.currentPassword.focus();
        });
        return false;
      }
      return true;
    },
    requestSettings() {
      const settings = JSON.parse(JSON.stringify(this.normalizedForm));
      if (this.smtpPassword !== '') settings.smtp.password = this.smtpPassword;
      return settings;
    },
    async testSmtp() {
      if (!this.validateForProtectedAction()) return;
      this.testing = true;
      this.message = '';
      try {
        const result = await this.sendRequest('smtpTest', {
          currentPassword: this.currentPassword,
          settings: JSON.stringify(this.requestSettings()),
        });
        this.applyResultSummary(result, false);
        this.lastTest = {
          at: result.test && result.test.testedAt
            ? result.test.testedAt : Math.floor(Date.now() / 1000),
          success: true,
          message: result.message || 'Die SMTP-Verbindung war erfolgreich.',
        };
        this.setMessage('success', result.message || 'Die SMTP-Verbindung war erfolgreich.');
      } catch (error) {
        this.lastTest = {
          at: Math.floor(Date.now() / 1000),
          success: false,
          message: error.message || 'Der SMTP-Test ist fehlgeschlagen.',
        };
        this.setMessage('error', this.lastTest.message);
      } finally {
        this.currentPassword = '';
        this.testing = false;
      }
    },
    async saveSettings() {
      if (!this.hasChanges) {
        this.setMessage('info', 'Es liegen keine Änderungen zum Speichern vor.');
        return;
      }
      if (!this.validateForProtectedAction()) return;

      this.saving = true;
      this.message = '';
      try {
        const result = await this.sendRequest('settingsSave', {
          currentPassword: this.currentPassword,
          settings: JSON.stringify(this.requestSettings()),
          expectedUpdatedAt: String(this.summary.updatedAt || 0),
        });
        this.applyResultSummary(result);
        this.loadedSnapshot = JSON.stringify(this.normalizedForm);
        this.smtpPassword = '';
        this.setMessage(
          'success',
          result.message || 'Die Mail-Einstellungen wurden sicher gespeichert.',
        );
      } catch (error) {
        this.setMessage(
          'error',
          error.message || 'Die Mail-Einstellungen konnten nicht gespeichert werden.',
        );
      } finally {
        this.currentPassword = '';
        this.saving = false;
      }
    },
    handleEditorError(message) {
      this.setMessage('error', message || 'Der Signaturinhalt konnte nicht eingefügt werden.');
    },
    formatDateTime(value, emptyText = 'Noch nicht gespeichert') {
      if (!value) return emptyText;
      let normalized = value;
      if (/^\d+$/.test(String(value))) {
        const timestamp = Number(value);
        normalized = timestamp < 1000000000000 ? timestamp * 1000 : timestamp;
      } else if (typeof value === 'string') {
        normalized = value.replace(
          /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/,
          '$1T$2',
        );
      }
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    },
  },
  created() {
    if (this.darfMailEinstellungenVerwalten) this.loadSettings();
  },
  beforeDestroy() {
    this.clearSecrets();
  },
};
</script>
